import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './extractTargetInfo';

export interface MethydTargetInfo {
  airPressureTendency: string;
  airPressure: string;
  airTemperature: string;
  averageWindSpeed: string;
  currentDirection2: string;
  currentDirection3: string;
  currentMeasuringLevel2: string;
  currentMeasuringLevel3: string;
  currentSpeed2: string;
  currentSpeed3: string;
  dewPoint: string;
  lastOrigin: string;
  location: string;
  message: string;
  metHydLatitude: string;
  metHydLongitude: string;
  latitude: number;
  longitude: number;
  metHydMmsi: string;
  metHydTimestamp: Date | null;
  relativeHumidity: string;
  reportTimestamp: Date | null;
  salinity: string;
  significantWaveHeight: string;
  surfaceCurrentDirection: string;
  surfaceCurrentSpeedIncludingTide: string;
  swellDirection: string;
  swellHeight: string;
  swellPeriod: string;
  waterLevelIncludingTide: string;
  waterLevelTrend: string;
  waterTemperature: string;
  waveDirection: string;
  wavePeriod: string;
  windDirection: string;
  windGustDirection: string;
  windGust: string;
}

const desiredTargetName: string[] = [
  'Air Pressure Tendency',
  'Air Pressure',
  'Air Temperature',
  'Average Wind Speed',
  'Current Direction #2',
  'Current Direction #3',
  'Current Measuring Level #2',
  'Current Measuring Level #3',
  'Current Speed #2',
  'Current Speed #3',
  'Dew Point',
  'Last origin',
  'Location',
  'Message',
  'MetHyd Latitude',
  'MetHyd Longitude',
  'MetHyd MMSI',
  'MetHyd timestamp',
  'Relative Humidity',
  'Report timestamp',
  'Salinity',
  'Significant Wave Height',
  'Surface Current Direction',
  'Surface Current Speed including tide',
  'Swell Direction',
  'Swell Height',
  'Swell Period',
  'Water Level including tide',
  'Water Level Trend',
  'Water Temperature',
  'Wave Direction',
  'Wave Period',
  'Wind Direction',
  'Wind Gust Direction',
  'Wind Gust',
];

export function extractMethydTargetInfos(
  result: QueryPointResult
): MethydTargetInfo[] {
  const methydTargetInfos: MethydTargetInfo[] = [];
  for (const target of result.targetInfo) {
    const rawMethydTargetInfo: { [key: string]: string } = {};
    for (const target_value of target.values) {
      // Here we map the values by their name, since they all have same ID
      if (desiredTargetName.includes(target_value.name)) {
        rawMethydTargetInfo[target_value.name] = target_value.value;
      }
    }
    const mmsi =
      target['mmsi'] ||
      rawMethydTargetInfo['MetHyd MMSI'] ||
      rawMethydTargetInfo['MMSI'];
    const methydInfo: MethydTargetInfo = {
      airPressureTendency: rawMethydTargetInfo['Air Pressure Tendency'],
      airPressure: rawMethydTargetInfo['Air Pressure'],
      airTemperature: rawMethydTargetInfo['Air Temperature'],
      averageWindSpeed: rawMethydTargetInfo['Average Wind Speed'],
      currentDirection2: rawMethydTargetInfo['Current Direction #2'],
      currentDirection3: rawMethydTargetInfo['Current Direction #3'],
      currentMeasuringLevel2: rawMethydTargetInfo['Current Measuring Level #2'],
      currentMeasuringLevel3: rawMethydTargetInfo['Current Measuring Level #3'],
      currentSpeed2: rawMethydTargetInfo['Current Speed #2'],
      currentSpeed3: rawMethydTargetInfo['Current Speed #3'],
      dewPoint: rawMethydTargetInfo['Dew Point'],
      lastOrigin: rawMethydTargetInfo['Last origin'],
      location: rawMethydTargetInfo['Location'],
      message: rawMethydTargetInfo['Message'],
      metHydLatitude: rawMethydTargetInfo['MetHyd Latitude'],
      metHydLongitude: rawMethydTargetInfo['MetHyd Longitude'],
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      metHydMmsi: mmsi,
      metHydTimestamp: rawMethydTargetInfo['MetHyd timestamp']
        ? parseDate(rawMethydTargetInfo['MetHyd timestamp'])
        : null,
      relativeHumidity: rawMethydTargetInfo['Relative Humidity'],
      reportTimestamp: parseDate(rawMethydTargetInfo['Report timestamp']),
      salinity: rawMethydTargetInfo['Salinity'],
      significantWaveHeight: rawMethydTargetInfo['Significant Wave Height'],
      surfaceCurrentDirection: rawMethydTargetInfo['Surface Current Direction'],
      surfaceCurrentSpeedIncludingTide:
        rawMethydTargetInfo['Surface Current Speed including tide'],
      swellDirection: rawMethydTargetInfo['Swell Direction'],
      swellHeight: rawMethydTargetInfo['Swell Height'],
      swellPeriod: rawMethydTargetInfo['Swell Period'],
      waterLevelIncludingTide:
        rawMethydTargetInfo['Water Level including tide'],
      waterLevelTrend: rawMethydTargetInfo['Water Level Trend'],
      waterTemperature: rawMethydTargetInfo['Water Temperature'],
      waveDirection: rawMethydTargetInfo['Wave Direction'],
      wavePeriod: rawMethydTargetInfo['Wave Period'],
      windDirection: rawMethydTargetInfo['Wind Direction'],
      windGustDirection: rawMethydTargetInfo['Wind Gust Direction'],
      windGust: rawMethydTargetInfo['Wind Gust'],
    };
    methydTargetInfos.push(methydInfo);
  }

  return methydTargetInfos;
}
